import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const FORM_ID = 'db6cm8kbe24agvuu3i70';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/db6cm8kbe24agvuu3i70';

const SCOPE_ITEMS = [
  { icon: 'ri-movie-2-line', title: '电影资源', desc: '汇集近期热门电影，提供类型、地区、年份多维度筛选。' },
  { icon: 'ri-tv-2-line', title: '电视剧集', desc: '精选热播与经典剧集，标注集数、连载状态与主演阵容。' },
  { icon: 'ri-newspaper-line', title: '影视资讯', desc: '行业动态、市场前瞻与深度解读，掌握影视圈最新风向。' },
  { icon: 'ri-search-line', title: '全站搜索', desc: '关键词一键检索影视作品与资讯，快速定位所需内容。' }
];

const USAGE_STEPS = [
  { step: '01', title: '浏览分类', desc: '通过顶部导航进入电影、电视剧或影视资讯分类，快速浏览内容。' },
  { step: '02', title: '筛选排序', desc: '在分类页按类型、地区、年份筛选，并按最新或热门排序。' },
  { step: '03', title: '查看详情', desc: '点击卡片进入详情页，了解导演、主演、简介等完整信息。' },
  { step: '04', title: '收藏与分享', desc: '收藏感兴趣的作品（保存在本地），或复制链接分享给朋友。' }
];

export default function About() {
  useDocumentTitle(
    '关于我们 - 风驰影院',
    '了解风驰影院的网站定位、内容范围与使用说明，并可通过联系表单与我们取得联系。'
  );

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get('website_alt') || '').trim();
    if (honey) {
      setStatus('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    const name = String(formData.get('name') || '').trim();
    const email = String(formData.get('email') || '').trim();
    const message = String(formData.get('message') || '').trim();

    if (!name || !email || !message) {
      setStatus('error');
      setFormError('请填写称呼、邮箱和留言内容后再提交。');
      return;
    }

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString()
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试。';

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg);
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，未能提交留言，请稍后重试。');
    }
  };

  return (
    <div className="w-full">
      <section className="relative overflow-hidden bg-primary-600">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20cinema%20concept%20background%2C%20soft%20bokeh%20lights%20and%20film%20strip%20curves%2C%20deep%20navy%20gradient%2C%20elegant%20minimal%20composition%2C%20no%20text&width=1600&height=500&seq=about-hero-bg&orientation=landscape"
            alt="关于风驰影院"
            className="h-full w-full object-cover object-top"
          />
        </div>
        <div className="relative mx-auto w-full max-w-[1240px] px-4 py-14 md:px-6 md:py-20">
          <nav className="flex items-center gap-1.5 text-xs text-primary-100 md:text-sm" aria-label="面包屑">
            <Link to="/" className="transition hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">关于我们</span>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-extrabold text-background-50 md:text-4xl">关于风驰影院</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-primary-100 md:text-base">
            专业的影视内容展示与资讯平台，围绕"最新电影电视剧播放与影视资讯"主题，为影视爱好者提供清晰、便捷的内容浏览体验。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1240px] px-4 py-10 md:px-6 md:py-14">
        {/* 网站定位 */}
        <section className="grid grid-cols-1 gap-8 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
              <i className="ri-focus-3-line text-primary-500" />
              网站定位
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-[15px]">
              风驰影院定位为专业影视内容展示与资讯平台，面向喜欢关注最新电影、电视剧资源及影视动态的观众群体。我们希望用户在浏览丰富的影视资源分类、查看作品详情与资讯时，能够快速筛选出自己感兴趣的类型，并获取准确、及时的相关信息。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-700 md:text-[15px]">
              需要特别说明的是，本站<strong className="text-foreground-900">不提供实际播放与下载功能</strong>，仅用于资源展示与信息查询演示，后续可视情况扩展接入播放接口。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h3 className="font-heading text-base font-bold text-foreground-950">核心数据</h3>
            <div className="mt-4 grid grid-cols-3 gap-4">
              {[
                { value: '20+', label: '影视作品' },
                { value: '8', label: '资讯专题' },
                { value: '3', label: '内容分类' }
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg bg-background-100 px-3 py-4 text-center">
                  <p className="font-heading text-2xl font-extrabold text-primary-600">{stat.value}</p>
                  <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 内容范围 */}
        <section className="mt-14">
          <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
            <i className="ri-stack-line text-primary-500" />
            内容范围
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SCOPE_ITEMS.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-2xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 使用说明 */}
        <section id="usage" className="mt-14 scroll-mt-24">
          <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
            <i className="ri-guide-line text-primary-500" />
            使用说明
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {USAGE_STEPS.map((item) => (
              <div key={item.step} className="relative rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="font-heading text-3xl font-extrabold text-background-300">{item.step}</span>
                <h3 className="mt-2 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 联系方式 + 表单 */}
        <section id="contact" className="mt-14 scroll-mt-24">
          <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
            <i className="ri-customer-service-2-line text-primary-500" />
            联系我们
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.2fr]">
            <div className="space-y-4">
              <a
                href="mailto:contact@fengchi-cinema.com"
                className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5 transition hover:border-primary-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <i className="ri-mail-line text-2xl" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-foreground-950">邮箱联系</span>
                  <span className="block text-sm text-foreground-500">contact@fengchi-cinema.com</span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-time-line text-2xl" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-foreground-950">服务时间</span>
                  <span className="block text-sm text-foreground-500">周一至周五 09:00 - 18:00</span>
                </span>
              </div>
              <p className="text-sm leading-relaxed text-foreground-500">
                如果你对网站内容有任何建议、合作意向，或发现信息有误，欢迎通过右侧表单给我们留言，我们会尽快回复。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h3 className="font-heading text-lg font-extrabold text-foreground-950">在线留言</h3>
              <form
                id="about-contact-form"
                data-readdy-form
                data-readdy-form-id={FORM_ID}
                action={SUBMIT_ADDR}
                method="post"
                onSubmit={handleSubmit}
                className="mt-5 space-y-4"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="about-name" className="mb-1.5 block text-sm font-medium text-foreground-700">
                      你的称呼
                    </label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      maxLength={40}
                      placeholder="请输入称呼"
                      className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="mb-1.5 block text-sm font-medium text-foreground-700">
                      邮箱
                    </label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      maxLength={80}
                      placeholder="name@example.com"
                      className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="about-subject" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    留言主题
                  </label>
                  <select
                    id="about-subject"
                    name="subject"
                    defaultValue="内容建议"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="合作洽谈">合作洽谈</option>
                    <option value="信息纠错">信息纠错</option>
                    <option value="其他问题">其他问题</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="about-message" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    留言内容
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请简要描述你的问题或建议（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="fc-auxiliary-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === 'error' && formError && (
                  <p className="rounded-md bg-accent-100 px-3 py-2 text-sm text-accent-900">{formError}</p>
                )}
                {status === 'success' && (
                  <p className="rounded-md bg-secondary-100 px-3 py-2 text-sm text-secondary-900">
                    留言已提交，感谢你的反馈，我们会尽快与你联系。
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="flex h-11 items-center justify-center gap-2 rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
                >
                  <i className="ri-send-plane-line text-lg" />
                  {status === 'submitting' ? '提交中...' : '提交留言'}
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}